<template>
  <div class="messages-table-wrapper">
    <!-- Search Panel -->
    <div class="search-panel">
      <div class="search-panel-header" @click="searchPanelOpen = !searchPanelOpen">
        <span class="search-panel-title">
          <span class="search-icon-bubble" :class="{ 'has-active': activeSearchCount > 0 }">
            <b-icon icon="magnify" size="is-small" />
          </span>
          Cautare
          <span v-if="activeSearchCount > 0" class="tag is-info is-light ml-2">
            {{ activeSearchCount }} activ{{ activeSearchCount > 1 ? 'e' : '' }}
          </span>
        </span>
        <b-icon :icon="searchPanelOpen ? 'chevron-up' : 'chevron-down'" size="is-small" />
      </div>

      <transition name="panel-slide">
        <div v-if="searchPanelOpen" class="search-panel-body">
          <div class="search-grid">
            <b-field label="Prenume">
              <b-input v-model="searchTerms.firstName" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
            <b-field label="Nume">
              <b-input v-model="searchTerms.lastName" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
            <b-field label="Email">
              <b-input v-model="searchTerms.email" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
            <b-field label="Telefon">
              <b-input v-model="searchTerms.phone" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
          </div>
          <div class="search-panel-actions">
            <button class="button is-small is-light" @click="clearSearch" :disabled="activeSearchCount === 0">
              <b-icon icon="close" size="is-small" />
              <span>Reseteaza</span>
            </button>
          </div>
        </div>
      </transition>
    </div>

    <!-- Table -->
    <b-table
      :loading="isLoading"
      :paginated="true"
      :backend-pagination="true"
      :total="total"
      :per-page="perPage"
      :current-page="currentPage"
      :striped="true"
      :hoverable="true"
      :data="filteredMessages"
      @page-change="$emit('page-change', $event)"
    >
      <b-table-column field="firstName" label="Prenume" sortable v-slot="props">
        {{ props.row.firstName }}
      </b-table-column>

      <b-table-column field="lastName" label="Nume" sortable v-slot="props">
        {{ props.row.lastName }}
      </b-table-column>

      <b-table-column field="email" label="Email" sortable v-slot="props">
        {{ props.row.email }}
      </b-table-column>

      <b-table-column field="phone" label="Telefon" sortable centered width="140" v-slot="props">
        {{ props.row.phone }}
      </b-table-column>

      <b-table-column field="date" label="Data" sortable centered width="120" v-slot="props">
        {{ props.row.date | formatDate }}
      </b-table-column>

      <!-- Actions -->
      <b-table-column centered width="80" v-slot="props">
        <div class="row-actions">
          <b-tooltip label="Vizualizeaza" position="is-top" type="is-dark" :delay="300">
            <button class="button is-small is-light preview-btn" @click.stop="openPreview(props.row)">
              <b-icon icon="eye" size="is-small" />
            </button>
          </b-tooltip>
        </div>
      </b-table-column>

      <section class="section" slot="empty">
        <div class="content has-text-grey has-text-centered">
          <template v-if="isLoading">
            <p><b-icon icon="dots-horizontal" size="is-large" /></p>
            <p>Incarcare data...</p>
          </template>
          <template v-else>
            <p><b-icon icon="emoticon-sad" size="is-large" /></p>
            <p>Nici o inregistrare&hellip;</p>
          </template>
        </div>
      </section>
    </b-table>

    <!-- Preview Modal -->
    <b-modal :active.sync="previewOpen" :width="560" scroll="keep" :can-cancel="['escape', 'outside']">
      <div class="preview-card" v-if="previewMessage">
        <div class="preview-header">
          <div class="preview-header-left">
            <span class="preview-name">{{ previewMessage.firstName }} {{ previewMessage.lastName }}</span>
            <span class="preview-date">{{ previewMessage.date | formatDate }}</span>
          </div>
        </div>

        <div class="preview-body">
          <div class="preview-field">
            <span class="preview-label">Prenume</span>
            <span class="preview-value">{{ previewMessage.firstName || '—' }}</span>
          </div>

          <div class="preview-field">
            <span class="preview-label">Nume</span>
            <span class="preview-value">{{ previewMessage.lastName || '—' }}</span>
          </div>

          <div class="preview-field">
            <span class="preview-label">Email</span>
            <span class="preview-value">{{ previewMessage.email || '—' }}</span>
          </div>

          <div class="preview-field">
            <span class="preview-label">Telefon</span>
            <span class="preview-value">{{ previewMessage.phone || '—' }}</span>
          </div>

          <div class="preview-field preview-field--full">
            <span class="preview-label">Mesaj</span>
            <span class="preview-value preview-value--message">{{ previewMessage.message || '—' }}</span>
          </div>
        </div>

        <div class="preview-footer">
          <button class="button is-light" @click="previewOpen = false">Inchide</button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import debounce from "lodash/debounce";

const SEARCH_FIELDS = ["firstName", "lastName", "email", "phone"];

export default {
  name: "MessagesTable",
  props: {
    messages: { type: Array, default: null },
    isLoading: { type: Boolean, default: false },
    total: { type: Number, default: 0 },
    currentPage: { type: Number, default: 1 },
  },
  data() {
    return {
      perPage: 20,
      searchTerms: Object.fromEntries(SEARCH_FIELDS.map((f) => [f, ""])),
      searchPanelOpen: false,
      previewOpen: false,
      previewMessage: null,
    };
  },
  computed: {
    activeSearchCount() {
      return Object.values(this.searchTerms).filter((v) => v && v.trim() !== "").length;
    },
    filteredMessages() {
      if (!this.messages) return [];
      return this.messages
        .filter((row) =>
          SEARCH_FIELDS.every((field) => {
            const term = this.searchTerms[field];
            if (!term || term.trim() === "") return true;
            return String(row[field] || "").toLowerCase().includes(term.toLowerCase());
          })
        )
        .slice()
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    },
  },
  created() {
    this.emitSearch = debounce(() => {
      this.$emit("search", { ...this.searchTerms });
    }, 300);
  },
  methods: {
    onSearchInput() {
      this.emitSearch();
    },
    clearSearch() {
      SEARCH_FIELDS.forEach((f) => this.$set(this.searchTerms, f, ""));
      this.$emit("search", { ...this.searchTerms });
    },
    openPreview(row) {
      this.previewMessage = row;
      this.previewOpen = true;
    },
  },
};
</script>

<style scoped>
/* Search Panel */
.search-panel {
  border: 1px solid #dbdbdb;
  border-radius: 6px;
  margin-bottom: 1rem;
  background: #fff;
  overflow: hidden;
}

.search-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 1rem;
  cursor: pointer;
  user-select: none;
  background: #fafafa;
  transition: background 0.15s;
}

.search-panel-header:hover {
  background: #f0f0f0;
}

.search-panel-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
}

.search-icon-bubble {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #3273dc;
  color: #fff;
  flex-shrink: 0;
  transition: background-color 0.2s;
}

.search-icon-bubble.has-active {
  background-color: #2160c4;
  box-shadow: 0 0 0 3px rgba(50, 115, 220, 0.25);
}

.search-panel-body {
  padding: 1rem 1rem 0.75rem;
  border-top: 1px solid #f0f0f0;
}

.search-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.5rem 1rem;
}

.search-grid :deep(.label) {
  font-size: 0.75rem;
  margin-bottom: 0.25rem;
}

.search-panel-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
}

.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.panel-slide-enter,
.panel-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* Table */
.message-cell {
  max-width: 380px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.row-actions {
  display: flex;
  gap: 0.35rem;
  justify-content: center;
}

.preview-btn:hover {
  background-color: #e8f0fe;
  color: #3273dc;
  border-color: #3273dc;
}

/* Preview Modal */
.preview-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #ededed;
}

.preview-header-left {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.preview-name {
  font-size: 1.15rem;
  font-weight: 700;
  color: #363636;
}

.preview-date {
  font-size: 0.8rem;
  color: #7a7a7a;
}

.preview-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.5rem;
  padding: 1.25rem 1.5rem;
}

.preview-field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.preview-field--full {
  grid-column: 1 / -1;
}

.preview-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9a9a9a;
}

.preview-value {
  font-size: 0.92rem;
  color: #363636;
  line-height: 1.45;
}

.preview-value--message {
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 300px;
  overflow-y: auto;
  background: #f8f9fa;
  border: 1px solid #ededed;
  border-radius: 4px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  line-height: 1.6;
}

.preview-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #ededed;
  background: #fafafa;
}
</style>
